import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLayoutEffect, useState, useSyncExternalStore, type JSX } from "react";
import { keepIfSame, perFrame } from "./measure";

/** The text a host shows at the caret: dictation in progress, a suggestion. Never in the document. */
export interface GhostStore {
  subscribe(listener: () => void): () => void;
  get(): string;
  set(text: string): void;
}

export function createGhostStore(): GhostStore {
  let text = "";
  const listeners = new Set<() => void>();
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    get: () => text,
    set(next) {
      if (next === text) return;
      text = next;
      for (const listener of listeners) listener();
    },
  };
}

interface Point {
  left: number;
  top: number;
  height: number;
}

/** Where the caret is, relative to the editor's own box, or `null` when it is not in the editor. */
function measure(root: HTMLElement): Point | null {
  const box = root.closest(".ze");
  const selection = root.ownerDocument.defaultView?.getSelection();
  if (box === null || selection === null || selection === undefined || selection.rangeCount === 0) {
    return null;
  }
  const range = selection.getRangeAt(0).cloneRange();
  if (!root.contains(range.endContainer)) return null;
  range.collapse(false);
  let rect = range.getBoundingClientRect();
  if (rect.height === 0) {
    // A caret in an empty block has no box of its own.
    const node = range.endContainer;
    const element = node instanceof Element ? node : node.parentElement;
    if (element === null) return null;
    rect = element.getBoundingClientRect();
  }
  const origin = box.getBoundingClientRect();
  return { left: rect.left - origin.left, top: rect.top - origin.top, height: rect.height };
}

/**
 * Draws the ghost text over the editor at the caret, outside the editable element: it is never part
 * of the document, of history, or of what is saved, and Lexical's DOM is never touched.
 */
export function GhostText({ store }: { store: GhostStore }): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const text = useSyncExternalStore(store.subscribe, store.get, store.get);
  const [point, setPoint] = useState<Point | null>(null);

  useLayoutEffect(() => {
    if (text === "") return;
    const refresh = (): void => {
      const root = editor.getRootElement();
      // The same point keeps the current state: an update that leaves the caret put renders nothing.
      setPoint(keepIfSame(root === null ? null : measure(root)));
    };
    refresh();
    const frame = perFrame(refresh);
    const offUpdate = editor.registerUpdateListener(refresh);
    const view = editor.getRootElement()?.ownerDocument.defaultView;
    view?.addEventListener("resize", frame.schedule);
    return () => {
      offUpdate();
      frame.cancel();
      view?.removeEventListener("resize", frame.schedule);
    };
  }, [editor, text]);

  if (text === "" || point === null) return null;
  return (
    <span
      className="ze-ghost-text"
      aria-hidden="true"
      data-ghost-text=""
      style={{ left: point.left, top: point.top, lineHeight: `${point.height}px` }}
    >
      {text}
    </span>
  );
}
